"use client";

import type { AuthParams, HFNAuthElement } from "../types/hfnauth-types";
import { requireEnv } from "../lib/env";

const HFN_TAG = "hfn-auth";

export function queryHFNElement(): HFNAuthElement | null {
  return document.querySelector(HFN_TAG) as unknown as HFNAuthElement | null;
}

export function getAuthParams(): AuthParams {
  return {
    authUrl: requireEnv("NEXT_PUBLIC_HFN_AUTH_URL", process.env.NEXT_PUBLIC_HFN_AUTH_URL),
    realm: process.env.NEXT_PUBLIC_HFN_REALM || "heartfulness",
    client_id: requireEnv("NEXT_PUBLIC_HFN_CLIENT_ID", process.env.NEXT_PUBLIC_HFN_CLIENT_ID),
  };
}

/**
 * Fetch a fresh Keycloak access token for the current session, on demand.
 * Returns null (never throws) when the user isn't logged in or the refresh
 * fails, so callers can fall back to the pre-Keycloak request shape during
 * the migration's transition window.
 */
async function refreshAccessToken(): Promise<string | null> {
  try {
    const { getRefreshToken } = await import("hfnauth/main");
    const params = getAuthParams();
    const tokenData = await getRefreshToken(params).catch(() => null);

    if (
      tokenData &&
      typeof tokenData === "object" &&
      "access_token" in tokenData &&
      typeof tokenData.access_token === "string"
    ) {
      return tokenData.access_token;
    }
    return null;
  } catch {
    return null;
  }
}

const TOKEN_MIN_REMAINING_MS = 60_000;

/** The stored access token, if it's still valid for at least a minute. */
function readStoredAccessToken(): string | null {
  try {
    const raw = localStorage.getItem("hfnAuthInfo");
    if (!raw) return null;
    const token = JSON.parse(atob(raw))?.access_token;
    if (typeof token !== "string") return null;
    const payload = JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
    if (typeof payload.exp !== "number") return null;
    return payload.exp * 1000 - Date.now() > TOKEN_MIN_REMAINING_MS ? token : null;
  } catch {
    return null;
  }
}

let refreshInFlight: Promise<string | null> | null = null;

/**
 * Access token for the current session. Reuses the stored token while it's
 * valid; otherwise refreshes once and shares that refresh with concurrent
 * callers (parallel refreshes with one refresh token get rejected).
 */
export async function getUserAccessToken(): Promise<string | null> {
  if (typeof window !== "undefined") {
    if (!localStorage.getItem("hfnAuthInfo")) return null; // signed out
    const stored = readStoredAccessToken();
    if (stored) return stored;
  }
  if (!refreshInFlight) {
    refreshInFlight = refreshAccessToken().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

export default {
  queryHFNElement,
  getAuthParams,
  getUserAccessToken,
};
